import { Select, type ComboboxItem, type OptionsFilter } from '@mantine/core';
import type { ReactNode } from 'react';
import { rowLabel } from '../../domain/rowLabel.js';
import { RecordLabel } from '../Diff/RecordLabel.js';

export type StudioOption = { id: string; title: string | null };

/** A studio option matches on its name or its id, case-insensitive. */
const byLabelOrId: OptionsFilter = ({ options, search }) => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return (options as ComboboxItem[]).filter(
        (option) => option.label.toLowerCase().includes(needle) || option.value.toLowerCase().includes(needle),
    );
};

/**
 * The studio picker: searchable by name or id, each option shown per **Record labels**
 * (`RecordLabel`). Shared by the Games page's filter (clearable, no label, a leftSection icon)
 * and the game editor's required field (`label`, `error`, `onBlur` for validate-on-blur) — the
 * options list, the filter and the option rendering are the same picker either way.
 */
export const StudioSelect = ({
    options,
    value,
    onChange,
    onBlur,
    error,
    label,
    required,
    placeholder = 'Pick a studio',
    clearable,
    leftSection,
    'aria-label': ariaLabel,
    w,
}: {
    options: StudioOption[];
    value: string | null;
    onChange: (value: string | null) => void;
    onBlur?: () => void;
    error?: string;
    label?: string;
    required?: boolean;
    placeholder?: string;
    clearable?: boolean;
    leftSection?: ReactNode;
    'aria-label'?: string;
    w?: number | string;
}) => (
    <Select
        label={label}
        required={required}
        aria-label={ariaLabel}
        w={w}
        leftSection={leftSection}
        placeholder={placeholder}
        data={options.map((option) => ({ value: option.id, label: rowLabel(option.title, option.id).label }))}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={error}
        searchable
        clearable={clearable}
        nothingFoundMessage="No studio"
        filter={byLabelOrId}
        renderOption={({ option }) => <RecordLabel title={option.label} entityKey={option.value} />}
        comboboxProps={{ withinPortal: true }}
    />
);
